/**
 * ASSIGN COLLECTIONS PAGE (Admin Tool)
 *
 * The inverse of CollectionsCleanup — that one MOVES values OUT of
 * `collectionName` (into tags, or normalizes variants). This one FILLS
 * IN values on items that are missing them. The result on brand pages:
 * fewer polishes languishing under "Uncategorized" / "Core / Permanent",
 * more polishes correctly attributed to their release collection.
 *
 * Flow:
 *   1. Admin picks a brand (text input, same as PolishNamesCleanup).
 *   2. Tool scans all items for that brand, groups by polish identity
 *      (brand+name), aggregates each polish's collectionName state:
 *        - how many items total across users
 *        - how many have collectionName set vs missing
 *        - which distinct values appear (as chips)
 *   3. Admin filters to polishes with at least one missing/core item
 *      (default), types a collection name per row, saves.
 *   4. Save updates only the items that are currently missing
 *      collectionName (or literally "Core / Permanent"). Existing
 *      user-assigned collection names are LEFT ALONE — we're filling
 *      gaps, not overwriting someone's personal label.
 *
 * Only user items (users/{uid}/items/*) are touched. Brand catalog
 * items and soft-deleted items are skipped.
 *
 * Auth-gated on `isAdmin === true` — same pattern as sibling admin
 * pages.
 */

import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  doc,
  getDoc,
  writeBatch,
} from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { ArrowLeft, Check, X } from 'lucide-react';
import { polishSlug } from '../utils/polishSlug';
import { fetchBrandUserItems } from '../utils/brandItemQuery';

/** Firestore batch cap; pad for headroom vs downstream trigger writes. */
const BATCH_LIMIT = 450;

/** Values we consider "not really a collection" — items in these buckets
 *  are candidates for being filled in. Kept as a set for O(1) lookup.
 *  The empty string covers items whose collectionName field is missing
 *  altogether (we treat missing === ''). "Core / Permanent" is the
 *  aggregation-computed display value for items that either mark
 *  releaseType:'core' or have no collection metadata at all. */
const FILLABLE_VALUES = new Set(['', 'core / permanent', 'uncategorized', 'core']);

interface PolishRow {
  /** Doc-id-style identity: `${brandSlug}__${nameSlug}`. Stable across
   *  the session so React keys don't churn if we reload. */
  slug: string;
  /** Human-readable name as first seen — preserves casing so the row
   *  reads how the polish actually got written. */
  name: string;
  /** The brand string as first seen on an item (preferred over user
   *  input for casing). */
  brand: string;
  /** Total user items for this polish across all collectors. */
  totalItems: number;
  /** Items whose collectionName is missing or Core / Permanent — the
   *  set that would be affected by a save on this row. */
  fillableItems: number;
  /** Distinct non-fillable collectionName values seen for this polish,
   *  sorted by count desc. Displayed as chips so admin can see whether
   *  a collection already exists and just needs the missing items
   *  filled in, or whether it truly is a new-collection assignment. */
  existingCollections: Array<{ name: string; count: number }>;
  /** Document refs of the fillable items — kept on the row so the save
   *  batches don't need a re-scan. */
  fillableRefs: Array<ReturnType<typeof doc>>;
}

export default function AssignCollections() {
  const [currentUser, setCurrentUser] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);

  const [brandInput, setBrandInput] = useState('');
  const [loadedBrand, setLoadedBrand] = useState<string | null>(null);
  const [rows, setRows] = useState<PolishRow[]>([]);
  const [loading, setLoading] = useState(false);

  const [filter, setFilter] = useState('');
  const [onlyFillable, setOnlyFillable] = useState(true);

  /** Per-row draft collectionName the admin has typed in but not yet
   *  saved. Keyed by polish slug. Empty string / absent = no pending
   *  change for that row. */
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [isRunning, setIsRunning] = useState(false);
  const [log, setLog] = useState<string[]>([]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user?.uid || null);
      if (user) {
        const snap = await getDoc(doc(db, 'users', user.uid));
        setIsAdmin(snap.exists() && snap.data()?.isAdmin === true);
      } else {
        setIsAdmin(false);
      }
      setCheckingAdmin(false);
    });
    return () => unsub();
  }, []);

  const say = (m: string) => {
    setLog((prev) => [...prev, m]);
    console.log('[AssignCollections]', m);
  };

  const loadBrand = async () => {
    const brand = brandInput.trim();
    if (!brand) {
      say('Enter a brand name first.');
      return;
    }
    setLoading(true);
    setLog([]);
    setDrafts({});
    say(`Loading items for brand "${brand}"…`);
    try {
      // Use the robust polishCatalog-based lookup so brands with
      // punctuation (e.g. "Polish ALL the Things!") and legacy items
      // whose brandLower field is inconsistent still match. Filters
      // are unioned; user-item filter applied downstream.
      const docs = await fetchBrandUserItems(brand);

      // Group by polish identity, collecting per-polish stats + refs of
      // items that would be affected by a save. One pass over the item
      // stream keeps this cheap even for large brands.
      const bySlug = new Map<
        string,
        {
          slug: string;
          name: string;
          brand: string;
          totalItems: number;
          fillableItems: number;
          fillableRefs: Array<ReturnType<typeof doc>>;
          collections: Map<string, number>;
        }
      >();

      let scanned = 0;
      let skippedNonUser = 0;
      let skippedDeleted = 0;
      let skippedNoName = 0;

      for (const d of docs) {
        scanned++;
        if (d.ref.parent.parent?.parent?.id !== 'users') {
          skippedNonUser++;
          continue;
        }
        const data = d.data() as Record<string, unknown>;
        if (data.isDeleted === true) {
          skippedDeleted++;
          continue;
        }
        const itemName =
          typeof data.name === 'string' ? data.name.trim() : '';
        if (!itemName) {
          skippedNoName++;
          continue;
        }
        const itemBrand =
          typeof data.brand === 'string' && data.brand.trim()
            ? data.brand.trim()
            : brand;
        const slug =
          (typeof data.slug === 'string' && data.slug) ||
          polishSlug(itemBrand, itemName);

        let entry = bySlug.get(slug);
        if (!entry) {
          entry = {
            slug,
            name: itemName,
            brand: itemBrand,
            totalItems: 0,
            fillableItems: 0,
            fillableRefs: [],
            collections: new Map(),
          };
          bySlug.set(slug, entry);
        }
        entry.totalItems++;

        const collectionName =
          typeof data.collectionName === 'string'
            ? data.collectionName.trim()
            : '';
        // Two flavors of "fillable" — genuinely missing/empty, and the
        // literal fallback strings that appear when a user typed "Core"
        // etc. Aggregation-computed "Core / Permanent" doesn't live on
        // the doc directly (it's derived), so we also treat items with
        // releaseType:'core' as fillable when they have no explicit
        // collectionName.
        const releaseType =
          typeof data.releaseType === 'string' ? data.releaseType : '';
        const isFillable =
          !collectionName ||
          FILLABLE_VALUES.has(collectionName.toLowerCase()) ||
          (releaseType === 'core' && !collectionName);
        if (isFillable) {
          entry.fillableItems++;
          entry.fillableRefs.push(d.ref);
        } else {
          entry.collections.set(
            collectionName,
            (entry.collections.get(collectionName) ?? 0) + 1
          );
        }
      }

      const asRows: PolishRow[] = Array.from(bySlug.values())
        .map((e) => ({
          slug: e.slug,
          name: e.name,
          brand: e.brand,
          totalItems: e.totalItems,
          fillableItems: e.fillableItems,
          fillableRefs: e.fillableRefs,
          existingCollections: Array.from(e.collections.entries())
            .map(([name, count]) => ({ name, count }))
            .sort(
              (a, b) => b.count - a.count || a.name.localeCompare(b.name)
            ),
        }))
        .sort(
          (a, b) =>
            b.fillableItems - a.fillableItems ||
            a.name.localeCompare(b.name)
        );

      setRows(asRows);
      setLoadedBrand(brand);
      say(
        `Loaded ${asRows.length} polishes for "${brand}" (${scanned} items considered, ${skippedNonUser} non-user, ${skippedDeleted} deleted, ${skippedNoName} unnamed skipped).`
      );
      const totalFillable = asRows.reduce((n, r) => n + r.fillableItems, 0);
      say(
        `${totalFillable} item${totalFillable === 1 ? '' : 's'} across ${asRows.filter((r) => r.fillableItems > 0).length} polish${asRows.filter((r) => r.fillableItems > 0).length === 1 ? '' : 'es'} could have a collection filled in.`
      );
    } catch (err) {
      say(`Load failed: ${(err as Error).message}`);
    } finally {
      setLoading(false);
    }
  };

  const filteredRows = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (onlyFillable && r.fillableItems === 0) return false;
      if (f && !r.name.toLowerCase().includes(f)) return false;
      return true;
    });
  }, [rows, filter, onlyFillable]);

  const setDraft = (slug: string, value: string) => {
    setDrafts((prev) => ({ ...prev, [slug]: value }));
  };

  /** Save a single row's drafted collectionName to every fillable item
   *  for that polish. Non-fillable items (users who set their own
   *  collectionName) are left alone. */
  const saveRow = async (row: PolishRow) => {
    const target = (drafts[row.slug] ?? '').trim();
    if (!target) {
      say(`"${row.name}" — no collection name typed; skipped.`);
      return;
    }
    if (row.fillableItems === 0) {
      say(`"${row.name}" — no fillable items; nothing to do.`);
      return;
    }
    setIsRunning(true);
    try {
      let batch = writeBatch(db);
      let ops = 0;
      let written = 0;
      for (const ref of row.fillableRefs) {
        batch.update(ref, { collectionName: target });
        ops++;
        written++;
        if (ops >= BATCH_LIMIT) {
          await batch.commit();
          batch = writeBatch(db);
          ops = 0;
        }
      }
      if (ops > 0) await batch.commit();
      say(
        `"${row.name}" → collectionName "${target}" on ${written} item${written === 1 ? '' : 's'}.`
      );
      // Clear this draft and reload the brand list so the row's counts
      // reflect the write (fillable count drops to 0, collection chip
      // appears).
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[row.slug];
        return next;
      });
      await loadBrand();
    } catch (err) {
      say(`Save failed for "${row.name}": ${(err as Error).message}`);
    } finally {
      setIsRunning(false);
    }
  };

  /** Save every row whose draft input is non-empty. Sequential per row
   *  so the log stays readable and any single failure doesn't tank the
   *  rest. */
  const saveAll = async () => {
    const targets = filteredRows.filter(
      (r) => (drafts[r.slug] ?? '').trim().length > 0 && r.fillableItems > 0
    );
    if (targets.length === 0) {
      say('Nothing to save — no drafted collection names.');
      return;
    }
    say(`Saving ${targets.length} row${targets.length === 1 ? '' : 's'}…`);
    for (const row of targets) {
      await saveRow(row);
    }
    say('Done.');
  };

  if (checkingAdmin) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-glass-secondary">
        Checking access…
      </div>
    );
  }
  if (!currentUser) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-glass-secondary">
        Please sign in to use this admin tool.
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-glass-secondary">
        This page is admin-only.
      </div>
    );
  }

  const draftedCount = Object.values(drafts).filter(
    (v) => v.trim().length > 0
  ).length;

  return (
    <div className="max-w-5xl mx-auto p-6">
      <Link
        to="/admin"
        className="inline-flex items-center gap-1 text-glass-muted hover:text-glass mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Admin
      </Link>

      <h1 className="text-2xl font-bold text-glass mb-2">Assign Collections</h1>
      <p className="text-glass-secondary text-sm mb-3">
        Fill in <code>collectionName</code> on user items that are currently
        showing under "Uncategorized" or "Core / Permanent" on brand pages.
        Items that ALREADY have a collection set are left alone — this is a
        gap-filler, not an overwriter.
      </p>
      <div className="bg-slate-800/40 border border-slate-600 rounded-lg p-3 mb-6 text-glass-secondary text-xs space-y-1">
        <div>
          Pick a brand, load its polishes, and type a collection name into
          the row for any polish that should belong to a specific release.
          Existing per-polish collection chips (below the name) tell you
          what other collectors already assigned — click one to reuse it
          for the drafted input.
        </div>
        <div>
          Save affects only the fillable items shown in the amber count.
          Nothing else on the user's item changes.
        </div>
      </div>

      {/* Brand picker */}
      <div className="bg-slate-800/40 border border-slate-600 rounded-lg p-4 mb-4">
        <label className="block text-xs text-glass-muted mb-1">Brand</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={brandInput}
            onChange={(e) => setBrandInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') loadBrand();
            }}
            placeholder="e.g. Mooncat, Essie, Loaded Lacquer"
            disabled={loading || isRunning}
            className="flex-1 bg-slate-900 border border-slate-600 rounded px-3 py-2 text-glass"
          />
          <button
            onClick={loadBrand}
            disabled={loading || isRunning || !brandInput.trim()}
            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
          >
            {loading ? 'Loading…' : loadedBrand ? 'Reload' : 'Load polishes'}
          </button>
        </div>
      </div>

      {rows.length > 0 && (
        <>
          {/* Filter + view controls */}
          <div className="bg-slate-800/40 border border-slate-600 rounded-lg p-4 mb-4">
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-glass-muted mb-1">
                  Filter by polish name
                </label>
                <input
                  type="text"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="e.g. Willow, 404, Ocean"
                  className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-2 text-glass"
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-glass text-sm">
                  <input
                    type="checkbox"
                    checked={onlyFillable}
                    onChange={(e) => setOnlyFillable(e.target.checked)}
                  />
                  Only show polishes with fillable items
                </label>
              </div>
            </div>
            <div className="text-glass-secondary text-xs mt-3">
              {filteredRows.length} of {rows.length} polishes shown · {draftedCount} drafted
            </div>
          </div>

          {/* List */}
          <div className="bg-slate-900 border border-slate-700 rounded-lg mb-4 max-h-[600px] overflow-y-auto">
            {filteredRows.length === 0 ? (
              <div className="p-4 text-glass-muted text-sm">
                No polishes match. Uncheck "only fillable" to see all of
                this brand's polishes.
              </div>
            ) : (
              <ul>
                {filteredRows.map((r) => {
                  const draft = drafts[r.slug] ?? '';
                  return (
                    <li
                      key={r.slug}
                      className="flex items-start gap-3 px-4 py-3 border-b border-slate-800"
                    >
                      <div className="flex-1 min-w-0">
                        <div
                          className="text-glass text-sm font-medium truncate"
                          title={r.name}
                        >
                          {r.name}
                        </div>
                        <div className="text-glass-muted text-[10px] mt-0.5">
                          slug: <code>{r.slug}</code>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          <span
                            className="inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-700 text-glass-secondary"
                            title={`${r.totalItems} user item${r.totalItems === 1 ? '' : 's'} across all collectors`}
                          >
                            {r.totalItems} owner{r.totalItems === 1 ? '' : 's'}
                          </span>
                          {r.fillableItems > 0 && (
                            <span
                              className="inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300"
                              title="Items with no collectionName set. Save fills only these."
                            >
                              {r.fillableItems} missing collection
                            </span>
                          )}
                          {r.existingCollections.map((c) => (
                            <button
                              key={c.name}
                              onClick={() => setDraft(r.slug, c.name)}
                              className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30"
                              title={`${c.count} user${c.count === 1 ? '' : 's'} already have this. Click to fill drafted input.`}
                            >
                              {c.name}
                              <span className="opacity-70">· {c.count}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="w-64 flex-shrink-0">
                        <input
                          type="text"
                          value={draft}
                          onChange={(e) => setDraft(r.slug, e.target.value)}
                          placeholder={r.fillableItems > 0 ? 'Collection name' : 'nothing to fill'}
                          disabled={isRunning || r.fillableItems === 0}
                          className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-glass text-sm disabled:opacity-50"
                        />
                      </div>
                      <div className="flex items-center gap-1 pt-0.5">
                        <button
                          onClick={() => saveRow(r)}
                          disabled={
                            isRunning || !draft.trim() || r.fillableItems === 0
                          }
                          title="Save this row"
                          className="p-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-30"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDraft(r.slug, '')}
                          disabled={isRunning || !draft}
                          title="Clear drafted value"
                          className="p-1.5 rounded bg-slate-600 text-white hover:bg-slate-500 disabled:opacity-30"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="flex gap-2 mb-4">
            <button
              onClick={saveAll}
              disabled={isRunning || draftedCount === 0}
              className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
            >
              {isRunning ? 'Saving…' : `Save all drafted (${draftedCount})`}
            </button>
          </div>
        </>
      )}

      {log.length > 0 && (
        <div className="bg-slate-900 border border-slate-700 rounded-lg p-4">
          <h3 className="text-glass-secondary text-sm mb-2">Log</h3>
          <pre className="text-xs text-glass-muted whitespace-pre-wrap">
            {log.join('\n')}
          </pre>
        </div>
      )}
    </div>
  );
}
